import { useEffect, useCallback, useRef } from 'react';
import { DISTANCE_LABELS_LAYER, CITY_ZOOM_THRESHOLD } from '../constants';

/**
 * Hook for automatically hiding distance labels at city zoom levels
 * @param {Object} map - Mapbox map instance
 * @param {boolean} mapReady - Whether map is ready
 * @param {boolean} userControlledVisibility - User's preference from layer control
 * @param {Function} onAutoVisibilityChange - Callback when auto visibility changes
 * @param {Function} onUserPreferenceChange - Callback when user preference should be updated
 * @returns {Object} Auto-hide state and controls
 */
export const useAutoHideDistanceLabels = (
  map, 
  mapReady, 
  userControlledVisibility,
  onAutoVisibilityChange,
  onUserPreferenceChange
) => {
  // Use imported zoom threshold constant
  
  // Current auto-hide state
  const autoHiddenRef = useRef(false);
  const lastZoomRef = useRef(null);
  const userPreferenceRef = useRef(userControlledVisibility);
  
  // Update user preference reference
  useEffect(() => {
    userPreferenceRef.current = userControlledVisibility;
  }, [userControlledVisibility]);

  // Check if distance labels should be auto-hidden
  const shouldAutoHide = useCallback((zoom) => {
    return zoom <= CITY_ZOOM_THRESHOLD;
  }, []);

  // Update distance labels visibility
  const updateDistanceLabelsVisibility = useCallback((zoom, force = false) => {
    if (!map || !mapReady) return;

    const shouldHide = shouldAutoHide(zoom);
    
    // Skip if zoom level hasn't crossed threshold and not forced update
    if (!force && 
        lastZoomRef.current !== null && 
        shouldAutoHide(lastZoomRef.current) === shouldHide) {
      return;
    }

    try {
      if (map.getLayer(DISTANCE_LABELS_LAYER)) {
        let finalVisibility;
        
        if (shouldHide) {
          // At city level, unconditionally force hide distance labels
          finalVisibility = 'none';
          autoHiddenRef.current = true; // Auto-hidden
        } else {
          // At non-city level
          // If previously auto-hidden, now auto-enable display
          if (autoHiddenRef.current) {
            userPreferenceRef.current = true; // Auto-set user preference to enabled
            // Notify layer control to update state
            if (onUserPreferenceChange) {
              onUserPreferenceChange(true);
            }
          }
          
          finalVisibility = userPreferenceRef.current ? 'visible' : 'none';
          autoHiddenRef.current = false; // Non-city level is not considered auto-hidden
        }

        map.setLayoutProperty(DISTANCE_LABELS_LAYER, 'visibility', finalVisibility);

        // Notify external components of auto-visibility state changes
        if (onAutoVisibilityChange) {
          onAutoVisibilityChange({
            zoom,
            shouldAutoHide: shouldHide,
            isAutoHidden: autoHiddenRef.current,
            finalVisibility: finalVisibility === 'visible'
          });
        }
      }
    } catch (error) {
      console.error('Error updating distance labels auto-hide visibility:', error);
    }

    lastZoomRef.current = zoom;
  }, [map, mapReady, shouldAutoHide, onAutoVisibilityChange, onUserPreferenceChange]);

  // Listen to map zoom events
  useEffect(() => {
    if (!map || !mapReady) return;

    const handleZoom = () => {
      const currentZoom = map.getZoom();
      updateDistanceLabelsVisibility(currentZoom);
    };

    const handleZoomEnd = () => {
      const currentZoom = map.getZoom();
      updateDistanceLabelsVisibility(currentZoom);
    };

    // Initial setup
    const initialZoom = map.getZoom();
    updateDistanceLabelsVisibility(initialZoom, true);

    // Handle distance labels data update event
    const handleDistanceLabelsUpdated = () => {
      const currentZoom = map.getZoom();
      updateDistanceLabelsVisibility(currentZoom, true);
    };

    // Add event listeners - listen to zoom events and distance labels updates
    map.on('zoom', handleZoom);
    map.on('zoomend', handleZoomEnd);
    map.on('distanceLabelsUpdated', handleDistanceLabelsUpdated);

    return () => {
      if (map.off) {
        map.off('zoom', handleZoom);
        map.off('zoomend', handleZoomEnd);
        map.off('distanceLabelsUpdated', handleDistanceLabelsUpdated);
      }
    };
  }, [map, mapReady, updateDistanceLabelsVisibility]);

  // Immediately update visibility when user preference changes
  useEffect(() => {
    if (map && mapReady) {
      const currentZoom = map.getZoom();
      updateDistanceLabelsVisibility(currentZoom, true);
    }
  }, [userControlledVisibility, map, mapReady, updateDistanceLabelsVisibility]);

  // Manually force update visibility (for external calls)
  const forceUpdateVisibility = useCallback(() => {
    if (map && mapReady) {
      const currentZoom = map.getZoom();
      updateDistanceLabelsVisibility(currentZoom, true);
    }
  }, [map, mapReady, updateDistanceLabelsVisibility]);

  return {
    isAutoHidden: autoHiddenRef.current,
    cityZoomThreshold: CITY_ZOOM_THRESHOLD,
    shouldAutoHide: map ? shouldAutoHide(map.getZoom()) : false,
    forceUpdateVisibility
  };
};
